import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar, Clock, Users, ArrowLeft, ArrowRight, Save } from 'lucide-react';

interface TestSchedulingStepProps {
  testTitle?: string;
  testDuration?: number; // in minutes
  onBack: () => void;
  onScheduleAndCreate?: (scheduleData: any) => void;
  onCreateWithoutScheduling?: () => void;
  onNext?: (scheduleData: any) => void;
  loading?: boolean;
  showBackButton?: boolean;
  nextButtonText?: string;
  isStandalone?: boolean;
}

export const TestSchedulingStep: React.FC<TestSchedulingStepProps> = ({
  testTitle = 'Test',
  testDuration = 60, // default 60 minutes
  onBack,
  onScheduleAndCreate,
  onCreateWithoutScheduling,
  onNext,
  loading = false,
  showBackButton = true,
  nextButtonText = 'Continue to Assignment',
  isStandalone = false
}) => {
  const [scheduleType, setScheduleType] = useState<'draft' | 'scheduled' | 'on-demand'>('draft');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');

  const [onDemandTestEndDate, setOnDemandTestEndDate] = useState('');
  const [onDemandTestEndTime, setOnDemandTestEndTime] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Auto-populate defaults when component mounts or schedule type changes
  useEffect(() => {
    if (scheduleType === 'scheduled' && !startDate) {
      setScheduledDefaults();
    } else if (scheduleType === 'on-demand' && !onDemandTestEndDate) {
      setOnDemandDefaults();
    }
  }, [scheduleType]);

  // Calculate end time display when start time changes (for display only)
  const [calculatedEndTime, setCalculatedEndTime] = useState('');

  useEffect(() => {
    if (startDate && startTime && scheduleType === 'scheduled') {
      calculateEndTimeForDisplay();
    }
  }, [startDate, startTime, testDuration, scheduleType]);



  const validateScheduledTest = () => {
    const newErrors: Record<string, string> = {};

    if (!startDate) newErrors.startDate = 'Start date is required';
    if (!startTime) newErrors.startTime = 'Start time is required';

    if (startDate && startTime) {
      const startDateTime = new Date(`${startDate}T${startTime}`);

      // Allow scheduling from yesterday for immediate testing
      const now = new Date();
      const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000); // 24 hours ago
      if (startDateTime < oneDayAgo) {
        newErrors.startDate = 'Start date/time cannot be more than 24 hours in the past';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateOnDemandTest = () => {
    const newErrors: Record<string, string> = {};

    // Always require end date/time for on-demand tests
    if (!onDemandTestEndDate) {
      newErrors.onDemandTestEndDate = 'Available to date is required for on-demand tests';
    }
    if (!onDemandTestEndTime) {
      newErrors.onDemandTestEndTime = 'Available to time is required for on-demand tests';
    }

    // For on-demand tests (available from now), validate end time is in the future
    if (onDemandTestEndDate && onDemandTestEndTime) {
      const endDateTime = new Date(`${onDemandTestEndDate}T${onDemandTestEndTime}`);
      if (endDateTime <= new Date()) {
        newErrors.onDemandTestEndDate = 'Available to date/time must be in the future';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleScheduleAndCreate = () => {
    console.log('TestSchedulingStep: handleScheduleAndCreate called');
    console.log('TestSchedulingStep: scheduleType:', scheduleType);
    console.log('TestSchedulingStep: startDate:', startDate);
    console.log('TestSchedulingStep: startTime:', startTime);

    if (scheduleType === 'scheduled' && !validateScheduledTest()) {
      console.log('TestSchedulingStep: Scheduled test validation failed');
      return;
    }
    if (scheduleType === 'on-demand' && !validateOnDemandTest()) {
      console.log('TestSchedulingStep: On-demand test validation failed');
      return;
    }

    const scheduleData: any = {
      scheduleType
    };

    if (scheduleType === 'scheduled') {
      // Ensure time has seconds (HH:MM -> HH:MM:00)
      const timeWithSeconds = startTime.length === 5 ? `${startTime}:00` : startTime;
      // Create a proper Date object in local timezone and convert to ISO string
      const localDateTime = new Date(`${startDate}T${timeWithSeconds}`);

      // Validate the date is valid
      if (isNaN(localDateTime.getTime())) {
        console.error('Invalid date/time:', { startDate, startTime, timeWithSeconds });
        setErrors({ ...errors, startTime: 'Invalid date or time format' });
        return;
      }

      const utcDateTime = localDateTime.toISOString(); // Full ISO 8601: 2025-10-21T08:21:00.000Z
      scheduleData.startTime = utcDateTime;
      // End time will be calculated by backend based on test duration
      scheduleData.isOnDemandTest = false; // Explicitly set for scheduled tests
      console.log('TestSchedulingStep: Scheduled test data (local to UTC):', {
        localInput: `${startDate}T${timeWithSeconds}`,
        localDateTime: localDateTime.toString(),
        utcDateTime: utcDateTime,
        formattedForBackend: utcDateTime
      });
    } else if (scheduleType === 'on-demand') {
      scheduleData.isOnDemandTest = true;
      // For on-demand tests, start time is current time (handled by backend)
      // End time is when the test becomes unavailable
      if (onDemandTestEndDate && onDemandTestEndTime) {
        // Ensure time has seconds (HH:MM -> HH:MM:00)
        const endTimeWithSeconds = onDemandTestEndTime.length === 5 ? `${onDemandTestEndTime}:00` : onDemandTestEndTime;
        // Create a proper Date object in local timezone and convert to ISO string
        const localEndDateTime = new Date(`${onDemandTestEndDate}T${endTimeWithSeconds}`);

        // Validate the date is valid
        if (isNaN(localEndDateTime.getTime())) {
          console.error('Invalid date/time:', { onDemandTestEndDate, onDemandTestEndTime, endTimeWithSeconds });
          setErrors({ ...errors, onDemandTestEndTime: 'Invalid date or time format' });
          return;
        }

        const utcEndDateTime = localEndDateTime.toISOString(); // Full ISO 8601
        scheduleData.onDemandTestEndDate = utcEndDateTime;
        console.log('TestSchedulingStep: On-demand end time (local to UTC):', {
          localInput: `${onDemandTestEndDate}T${endTimeWithSeconds}`,
          localDateTime: localEndDateTime.toString(),
          utcDateTime: utcEndDateTime,
          formattedForBackend: utcEndDateTime
        });
      }
      console.log('TestSchedulingStep: On-demand test data:', scheduleData);
    } else {
      console.log('TestSchedulingStep: Draft test data:', scheduleData);
    }
    // Note: For 'draft' scheduleType, we don't set scheduling fields as it's handled differently

    if (isStandalone && onNext) {
      console.log('TestSchedulingStep: Calling onNext (standalone mode)');
      onNext(scheduleData);
    } else if (onScheduleAndCreate) {
      console.log('TestSchedulingStep: Calling onScheduleAndCreate');
      onScheduleAndCreate(scheduleData);
    }
  };

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getYesterdayDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  };

  const setScheduledDefaults = () => {
    const today = getTodayDate();
    const now = new Date();

    // Set default start time to current time + 5 minutes for immediate scheduling
    const defaultStartTime = new Date(now.getTime() + 5 * 60 * 1000);
    const startTimeString = defaultStartTime.toTimeString().slice(0, 5);

    setStartDate(today);
    setStartTime(startTimeString);
  };

  const calculateEndTimeForDisplay = () => {
    if (!startDate || !startTime) {
      setCalculatedEndTime('');
      return;
    }

    const startDateTime = new Date(`${startDate}T${startTime}`);
    const endDateTime = new Date(startDateTime.getTime() + (testDuration * 60 * 1000));

    const endDateString = endDateTime.toISOString().split('T')[0];
    const endTimeString = endDateTime.toTimeString().slice(0, 5);

    setCalculatedEndTime(`${endDateString} at ${endTimeString}`);
  };

  const handleStartDateChange = (value: string) => {
    setStartDate(value);
  };

  const handleStartTimeChange = (value: string) => {
    setStartTime(value);
  };

  const setOnDemandDefaults = () => {
    // Set default values for on-demand tests
    const now = new Date();
    const oneWeekLater = new Date(now.getTime() + (7 * 24 * 60 * 60 * 1000));

    // Available To: One week from now at 5:00 PM
    const endDate = oneWeekLater.toISOString().split('T')[0];
    setOnDemandTestEndDate(endDate);
    setOnDemandTestEndTime('17:00');
  };

  return (
    <div className="space-y-6">
      {!isStandalone && (
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Schedule Test</h1>
            <p className="text-muted-foreground">Configure when and how students can access "{testTitle}"</p>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Test Scheduling Options
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Schedule Type Selection */}
          <div className="space-y-4">
            <Label className="text-base font-medium">How would you like to schedule this test?</Label>

            <div className={`grid grid-cols-1 ${isStandalone ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-4`}>
              {!isStandalone && (
                <Card
                  className={`cursor-pointer transition-colors ${
                    scheduleType === 'draft' ? 'border-primary bg-primary/5' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => setScheduleType('draft')}
                >
                  <CardContent className="p-4 text-center">
                    <Save className="h-8 w-8 mx-auto mb-2 text-gray-600" />
                    <h3 className="font-medium">Save as Draft</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Create test without scheduling. You can schedule it later.
                    </p>
                  </CardContent>
                </Card>
              )}

              <Card
                className={`cursor-pointer transition-colors ${
                  scheduleType === 'scheduled' ? 'border-primary bg-primary/5' : 'hover:bg-gray-50'
                }`}
                onClick={() => setScheduleType('scheduled')}
              >
                <CardContent className="p-4 text-center">
                  <Clock className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                  <h3 className="font-medium">Schedule Test</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Set specific start and end times for the test.
                  </p>
                </CardContent>
              </Card>

              <Card
                className={`cursor-pointer transition-colors ${
                  scheduleType === 'on-demand' ? 'border-primary bg-primary/5' : 'hover:bg-gray-50'
                }`}
                onClick={() => setScheduleType('on-demand')}
              >
                <CardContent className="p-4 text-center">
                  <Users className="h-8 w-8 mx-auto mb-2 text-green-600" />
                  <h3 className="font-medium">On-Demand Test</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Allow students to take the test anytime within a flexible window.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Scheduled Test Configuration */}
          {scheduleType === 'scheduled' && (
            <div className="space-y-4 p-4 border rounded-lg bg-blue-50/50">
              <h4 className="font-medium text-blue-900">Scheduled Test Configuration</h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="startDate">Start Date</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => handleStartDateChange(e.target.value)}
                    min={getYesterdayDate()}
                    className={errors.startDate ? 'border-red-500' : ''}
                  />
                  {errors.startDate && <p className="text-sm text-red-600">{errors.startDate}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="startTime">Start Time</Label>
                  <Input
                    id="startTime"
                    type="time"
                    value={startTime}
                    onChange={(e) => handleStartTimeChange(e.target.value)}
                    className={errors.startTime ? 'border-red-500' : ''}
                  />
                  {errors.startTime && <p className="text-sm text-red-600">{errors.startTime}</p>}
                </div>
              </div>

              {/* Display calculated end time */}
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <Label className="text-sm font-medium text-green-700">Test End Time (Auto-calculated)</Label>
                {calculatedEndTime ? (
                  <div>
                    <p className="text-sm text-green-800 mt-1">
                      The test will automatically end at: <strong>{calculatedEndTime}</strong>
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      Based on test duration of {testDuration} minutes
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 mt-1">
                    End time will be calculated when start date and time are selected
                  </p>
                )}
              </div>
            </div>
          )}

          {/* On-Demand Test Configuration */}
          {scheduleType === 'on-demand' && (
            <div className="space-y-4 p-4 border rounded-lg bg-green-50/50">
              <h4 className="font-medium text-green-900">On-Demand Test Configuration</h4>

              {/* Available From Section */}
              <div className="space-y-4">
                <h5 className="font-medium text-green-800">Available From (Current Date/Time)</h5>
                <div className="p-3 bg-white rounded border">
                  <p className="text-sm text-gray-600">
                    This test will be available to students as soon as it is published.
                  </p>
                </div>
              </div>

              {/* Available To Section (always required for on-demand tests) */}
              <div className="space-y-4">
                <h5 className="font-medium text-green-800">Available To (Required)</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-white rounded border">
                  <div className="space-y-2">
                    <Label htmlFor="onDemandEndDate">Available To Date</Label>
                    <Input
                      id="onDemandEndDate"
                      type="date"
                      value={onDemandTestEndDate}
                      onChange={(e) => setOnDemandTestEndDate(e.target.value)}
                      min={getYesterdayDate()}
                      className={errors.onDemandTestEndDate ? 'border-red-500' : ''}
                    />
                    {errors.onDemandTestEndDate && <p className="text-sm text-red-600">{errors.onDemandTestEndDate}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="onDemandEndTime">Available To Time</Label>
                    <Input
                      id="onDemandEndTime"
                      type="time"
                      value={onDemandTestEndTime}
                      onChange={(e) => setOnDemandTestEndTime(e.target.value)}
                      className={errors.onDemandTestEndTime ? 'border-red-500' : ''}
                    />
                    {errors.onDemandTestEndTime && <p className="text-sm text-red-600">{errors.onDemandTestEndTime}</p>}
                  </div>
                </div>
              </div>

              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>On-Demand Test:</strong> Students can take this test anytime from now until the specified end date/time.
                  The test will be available immediately and remain accessible until the deadline.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex justify-between">
        {showBackButton && (
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {isStandalone ? 'Back' : 'Back to Preview'}
          </Button>
        )}

        <div className="flex gap-3">
          {scheduleType === 'draft' && !isStandalone && onCreateWithoutScheduling && (
            <Button onClick={onCreateWithoutScheduling} disabled={loading}>
              <Save className="mr-2 h-4 w-4" />
              Create as Draft
            </Button>
          )}

          {(scheduleType === 'scheduled' || scheduleType === 'on-demand') && (
            <Button onClick={handleScheduleAndCreate} disabled={loading}>
              <ArrowRight className="mr-2 h-4 w-4" />
              {nextButtonText}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
